import {
  mysqlTable,
  int,
  bigint,
  smallint,
  tinyint,
  varchar,
  text,
  longtext,
  double,
  decimal,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
  unique,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";

/**
 * Batch 1.4 — Leave (reskin-docs/04). leave_types + per-user quotas + applications + files +
 * the monthly summary (payroll input) + the LeavePolicy module (carry-forward/encashment/
 * clubbing). Ported line-by-line:
 *   base: leave_types L909–919, employee_leave_quotas L921–929, leaves L1666–… (+ the
 *   `manager_status_permission` enum added by 2022_09_13_075642).
 *   leave_files (2022_12_01_070705), monthly_leave_summaries (2026_06_18_020000),
 *   LeavePolicy module (Modules/LeavePolicy): leave_type_policies, leave_clubbing_rules,
 *   leave_carry_forward_runs, leave_carry_forward_results.
 * NOTE (doc 00): the carry-forward *run* dispatcher is stubbed in legacy — schema is real and
 * ported here; the math/cron is built in the leave feature step (reskin-docs/04).
 */

export const leaveTypes = mysqlTable("leave_types", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  typeName: varchar("type_name", { length: 255 }).notNull(),
  color: varchar("color", { length: 255 }).notNull(),
  // no_of_leaves changed int→double by 2024_10_19_092843_recalculate_leaves.
  noOfLeaves: double("no_of_leaves").default(5).notNull(),
  paid: boolean("paid").default(true).notNull(),
  // monthly_limit changed int→decimal(10,2) by 2024_04_17_064540.
  monthlyLimit: decimal("monthly_limit", { precision: 10, scale: 2 }).default("0").notNull(),
  // Added by 2023_02_27_081104_add_column_in_leave_type_table.
  effectiveAfter: int("effective_after"),
  effectiveType: varchar("effective_type", { length: 255 }),
  unusedLeave: varchar("unused_leave", { length: 255 }),
  encashed: boolean("encashed").notNull(),
  allowedProbation: boolean("allowed_probation").notNull(),
  allowedNotice: boolean("allowed_notice").notNull(),
  gender: varchar("gender", { length: 255 }),
  maritalStatus: varchar("marital_status", { length: 255 }),
  // department/designation/role changed string→longText by 2023_10_12_043341.
  department: longtext("department"),
  designation: longtext("designation"),
  role: longtext("role"),
  // Added by 2024_08_27_093025_add_over_utilization_column_leave_types.
  overUtilization: mysqlEnum("over_utilization", ["not_allowed", "allow_paid", "allow_unpaid"])
    .default("not_allowed")
    .notNull(),
  // Added by 2024_06_14_105841_add_column_in_leavetype_and_employeeleavequota_table.
  leavetype: mysqlEnum("leavetype", ["monthly", "yearly"]),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
  // softDeletes() added by 2024_02_13_094338_add_soft_delete_table_leave_types.
  deletedAt: timestamp("deleted_at"),
});

/** employee_leave_quotas — NO company_id (legacy). */
export const employeeLeaveQuotas = mysqlTable("employee_leave_quotas", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  leaveTypeId: int("leave_type_id", { unsigned: true })
    .notNull()
    .references(() => leaveTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
  // no_of_leaves changed int→double by 2022_12_01_070705 / 2024_10_19_092843.
  noOfLeaves: double("no_of_leaves").notNull(),
  // Added by 2024_06_14_105841.
  carryForwardStatus: text("carry_forward_status"),
  // Added by 2024_07_22_114153_add_leave_type_impact_to_employee_leave_quotas_table.
  leaveTypeImpact: boolean("leave_type_impact").default(false).notNull(),
  // Added by 2024_10_19_092843_recalculate_leaves.
  overutilisedLeaves: double("overutilised_leaves").default(0).notNull(),
  unusedLeaves: double("unused_leaves").default(0).notNull(),
  carryForwardLeaves: double("carry_forward_leaves").default(0).notNull(),
  carryForwardApplied: double("carry_forward_applied").default(0).notNull(),
  // Added by 2025_02_04_111756_add_leaves_to_reimburse_column.
  leavesToReimburse: int("leaves_to_reimburse").default(0).notNull(),
  leavesActuallyReimbursed: int("leaves_actually_reimbursed").default(0).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const leaves = mysqlTable(
  "leaves",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    leaveTypeId: int("leave_type_id", { unsigned: true })
      .notNull()
      .references(() => leaveTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
    duration: varchar("duration", { length: 255 }).notNull(),
    leaveDate: date("leave_date", { mode: "string" }).notNull(),
    reason: text("reason").notNull(),
    status: mysqlEnum("status", ["approved", "pending", "rejected"]).notNull(),
    rejectReason: text("reject_reason"),
    paid: boolean("paid").default(false).notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    eventId: text("event_id"),
    approvedBy: int("approved_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    approvedAt: datetime("approved_at"),
    halfDayType: varchar("half_day_type", { length: 255 }),
    // Added by 2022_09_13_075642 — two-step approval flag (reskin-docs/07 §2).
    managerStatusPermission: mysqlEnum("manager_status_permission", ["pre-approve", "approved"]),
    // Added by 2022_09_23_181722_add_approve_reason_column_to_leaves_table.
    approveReason: text("approve_reason"),
    // Added by 2022_12_13_112213_add_new_fields_in_employee_details_table.
    uniqueId: varchar("unique_id", { length: 255 }),
    // Added by 2024_08_27_093025_add_over_utilization_column_leave_types.
    overUtilized: boolean("over_utilized").default(false).notNull(),
    // Added by 2026_06_18_030000_add_apply_mode_urgency_to_leaves.
    applyMode: varchar("apply_mode", { length: 20 }),
    urgency: varchar("urgency", { length: 20 }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("leaves_leave_date_index").on(t.leaveDate)],
);

export const leaveFiles = mysqlTable("leave_files", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  leaveId: int("leave_id", { unsigned: true })
    .notNull()
    .references(() => leaves.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * monthly_leave_summaries — denormalized payroll input (paid/LOP/payable days per
 * payroll-entity+user+month). Legacy declares NO FK constraints here (only indexes) —
 * preserved. `payroll_entity_id` → company_payroll_entities (logical; Batch 1.5).
 */
export const monthlyLeaveSummaries = mysqlTable(
  "monthly_leave_summaries",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }),
    payrollEntityId: int("payroll_entity_id", { unsigned: true }).notNull(),
    userId: int("user_id", { unsigned: true }).notNull(),
    year: smallint("year").notNull(),
    month: tinyint("month").notNull(),
    totalDays: decimal("total_days", { precision: 6, scale: 2 }).default("0").notNull(),
    paidDays: decimal("paid_days", { precision: 6, scale: 2 }).default("0").notNull(),
    lopDays: decimal("lop_days", { precision: 6, scale: 2 }).default("0").notNull(),
    payableDays: decimal("payable_days", { precision: 6, scale: 2 }).default("0").notNull(),
    lockedBy: int("locked_by", { unsigned: true }),
    lockedAt: datetime("locked_at"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("monthly_leave_summaries_company_id_index").on(t.companyId),
    index("monthly_leave_summaries_payroll_entity_id_index").on(t.payrollEntityId),
    index("monthly_leave_summaries_user_id_index").on(t.userId),
    unique("mls_entity_user_period_uq").on(t.payrollEntityId, t.userId, t.year, t.month),
  ],
);

export const leaveTypePolicies = mysqlTable(
  "leave_type_policies",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    leaveTypeId: int("leave_type_id", { unsigned: true })
      .notNull()
      .references(() => leaveTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
    carryForwardCap: decimal("carry_forward_cap", { precision: 8, scale: 2 }),
    carryForwardExpiresAfterMonths: smallint("carry_forward_expires_after_months", {
      unsigned: true,
    }),
    encashCap: decimal("encash_cap", { precision: 8, scale: 2 }),
    encashAllowed: boolean("encash_allowed").default(false).notNull(),
    minBalanceForEncash: decimal("min_balance_for_encash", { precision: 8, scale: 2 })
      .default("0")
      .notNull(),
    clubbingPolicy: mysqlEnum("clubbing_policy", ["strict", "permissive"])
      .default("permissive")
      .notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [unique("ltp_unique_leave_type").on(t.companyId, t.leaveTypeId)],
);

export const leaveClubbingRules = mysqlTable(
  "leave_clubbing_rules",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    leaveTypeAId: int("leave_type_a_id", { unsigned: true })
      .notNull()
      .references(() => leaveTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
    leaveTypeBId: int("leave_type_b_id", { unsigned: true })
      .notNull()
      .references(() => leaveTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
    rule: mysqlEnum("rule", ["clubbable", "not_clubbable"]).notNull(),
    notes: text("notes"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [unique("lcr_unique_pair").on(t.companyId, t.leaveTypeAId, t.leaveTypeBId)],
);

export const leaveCarryForwardRuns = mysqlTable(
  "leave_carry_forward_runs",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    cycleYear: smallint("cycle_year", { unsigned: true }).notNull(),
    cycleStart: date("cycle_start").notNull(),
    cycleEnd: date("cycle_end").notNull(),
    mode: mysqlEnum("mode", ["dry_run", "commit"]).notNull(),
    status: mysqlEnum("status", ["pending", "running", "completed", "failed", "cancelled"])
      .default("pending")
      .notNull(),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    summaryJson: longtext("summary_json"),
    triggeredBy: int("triggered_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("lcfr_cycle_idx").on(t.companyId, t.cycleYear),
    index("lcfr_status_idx").on(t.status),
  ],
);

export const leaveCarryForwardResults = mysqlTable(
  "leave_carry_forward_results",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    runId: int("run_id", { unsigned: true })
      .notNull()
      .references(() => leaveCarryForwardRuns.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    leaveTypeId: int("leave_type_id", { unsigned: true })
      .notNull()
      .references(() => leaveTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
    openingBalance: decimal("opening_balance", { precision: 8, scale: 2 }).default("0").notNull(),
    earned: decimal("earned", { precision: 8, scale: 2 }).default("0").notNull(),
    used: decimal("used", { precision: 8, scale: 2 }).default("0").notNull(),
    preCapCarry: decimal("pre_cap_carry", { precision: 8, scale: 2 }).default("0").notNull(),
    capApplied: decimal("cap_applied", { precision: 8, scale: 2 }),
    carryForwardAmount: decimal("carry_forward_amount", { precision: 8, scale: 2 })
      .default("0")
      .notNull(),
    encashEligible: decimal("encash_eligible", { precision: 8, scale: 2 }).default("0").notNull(),
    applied: boolean("applied").default(false).notNull(),
    reversalOf: int("reversal_of", { unsigned: true }).references(
      (): AnyMySqlColumn => leaveCarryForwardResults.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    computedAt: timestamp("computed_at"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("lcfres_run_user_idx").on(t.runId, t.userId)],
);
